<template>
  <div v-if="props.hidden" class="d-none"></div>
  <div v-else>
    <form @submit.prevent="submitForm" :disabled="isLoading" class="row g-3 mx-0">
      <div class="input-group px-0">
        <input :value="modelValue" @input="onInput" type="text" class="form-control" size="12" id="input" ref="input" aria-describedby="inputHelp" :placeholder="props.instruction" :v-focus="!props.explicitFocus" />
        <button type="submit" class="btn btn-outline-primary" :disabled="isLoading">
          <font-awesome-icon :icon="['fas', 'barcode-read']" />
          <span class="d-none d-md-inline-block">&nbsp;{{ $t('action.scan') }}</span>
        </button>
      </div>
      <div v-if="props.helpText" id="inputHelp" class="form-text">{{ props.helpText }}</div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, PropType } from "vue";
import type IGS1 from "@/models/IGS1"; "@/models/IGS1";
import { parseBarcode } from "@/services/barcodeParser";

const props = defineProps({
  modelValue: { type: String, required: false, default: '' },
  instruction: { type: String, required: false },
  helpText: { type: String, required: false },
  explicitFocus: { type: Boolean, required: false, default: false, helpText: 'Explicitly focus the input field instead of doing so on mount' },
  gs1FieldSelector: { type: Function as PropType<(barcode: string, gs1: IGS1 | undefined) => string>, required: false },
  hidden: { type: Boolean, required: false, default: false, helpText: 'Hide the input field' }
});

const emit = defineEmits<{
  (e: 'scanned', rawInput: string, gs1: IGS1 | undefined, bcType: string | undefined): void,
  (e: 'update:modelValue', value: string): void,
}>();

const focus = () => {
  // input.value?.select();
  input.value?.focus();
};

defineExpose({ focus });

const isLoading = ref(false);
const input = ref<HTMLInputElement | null>(null);

// TODO: remove this when the window onScan event is declared in the global scope
declare const window: Window & typeof globalThis & MelodiWindowEventHandlers & { onScan: (scanEvent: ScanEvent) => void };

onMounted(() => {
  window.addEventListener('onScan', onScan);
});

onUnmounted(() => {
  window.removeEventListener('onScan', onScan);
});

const changeInputValue = (value: string) => {
  emit('update:modelValue', value);
}

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  changeInputValue(target.value);
};

const onScan = (scanEvent: WindowScanEvent) => {
  const scanData = scanEvent.detail.data;
  const scanType = scanEvent.detail.type;
  // let bc = barcode.value; // NOT WORKING for some reason
  let bc = scanData ?? '';
  if (!bc || bc.length === 0) { return; }

  // Remove duplicate prefix if scanner adds both symbol identifier and ]C1
  const gs1Prefixes = [']C1', ']e0', ']e1', ']e2', ']d2', ']Q3'];
  for (const prefix of gs1Prefixes) {
    if (bc.startsWith(prefix + ']C1')) {
      bc = prefix + bc.substring(prefix.length + 3);
      break;
    }
  }

  isLoading.value = true;

  let gs1 = undefined as IGS1 | undefined;

  try {
    var answer = parseBarcode(bc);
    if (answer.codeName === 'GS1-128' || answer.codeName === 'GS1 DataMatrix' || answer.codeName === 'GS1 DataBar' || answer.codeName === 'GS1 Composite' || answer.codeName === 'GS1 QR Code') {
      gs1 = {
        gtin: answer.parsedCodeItems.find((x) => x.ai == '01')?.data as string | undefined,
        content: answer.parsedCodeItems.find((x) => x.ai == '02')?.data as string | undefined,
        lot: answer.parsedCodeItems.find((x) => x.ai == '10')?.data as string | undefined,
        bestBefore: answer.parsedCodeItems.find((x) => x.ai == '15')?.data as Date | undefined,
        expiry: answer.parsedCodeItems.find((x) => x.ai == '17')?.data as Date | undefined,
        quantity: answer.parsedCodeItems.find((x) => x.ai == '37')?.data as number | undefined,
        itemCode: answer.parsedCodeItems.find((x) => x.ai == '91')?.data as string | undefined
      } as IGS1
    }
  } catch (error) {
    // ignore
    console.debug(error);
  }

  if (bc.length >= 3 && bc.startsWith(']')) {
    bc = bc.substring(3);
  }
  
  if (props.gs1FieldSelector) {
    changeInputValue(props.gs1FieldSelector(bc, gs1));
  }
  else {
    changeInputValue('');
  }

  isLoading.value = false;
  emit('scanned', bc, gs1, scanType);
};

const submitForm = () => {
  // let bc = barcode.value; // NOT WORKING for some reason
  let bc = input.value?.value ?? '';
  if (!bc) { return; }

  isLoading.value = true;

  let gs1 = undefined as IGS1 | undefined;

  try {
    const gs1_128_SymbolId = "]C1";
    const gs1_DataBar_SymbolId = "]e0";
    const gs1_Composite1_SymbolId = "]e1";
    const gs1_Composite2SymbolId = "]e2";
    const gs1_DataMatrix_SymbolId = "]d2";
    const gs1_QrCode_SymbolId = "]Q3";
    const fncChar = String.fromCharCode(29); // the ASCII "group separator"
    let bcMod = bc;
    // add missing GS1 start character if potential GS1-128 barcode
    if (
      !bcMod.startsWith(gs1_128_SymbolId) &&
      !bcMod.startsWith(gs1_DataBar_SymbolId) &&
      !bcMod.startsWith(gs1_Composite1_SymbolId) &&
      !bcMod.startsWith(gs1_Composite2SymbolId) &&
      !bcMod.startsWith(gs1_DataMatrix_SymbolId) &&
      !bcMod.startsWith(gs1_QrCode_SymbolId) &&
      (bcMod.length > 15 || bcMod.includes('@') || bcMod.includes(fncChar))) {
      bcMod = gs1_128_SymbolId + bcMod;
    }

    // replace @ from enterprise browser back with FNC1
    bcMod = bcMod.replace(/@/g, fncChar);

    var answer = parseBarcode(bcMod);
    if (answer.codeName === 'GS1-128' || answer.codeName === 'GS1 DataMatrix' || answer.codeName === 'GS1 DataBar' || answer.codeName === 'GS1 Composite' || answer.codeName === 'GS1 QR Code') {
      gs1 = {
        gtin: answer.parsedCodeItems.find((x) => x.ai == '01')?.data as string | undefined,
        content: answer.parsedCodeItems.find((x) => x.ai == '02')?.data as string | undefined,
        lot: answer.parsedCodeItems.find((x) => x.ai == '10')?.data as string | undefined,
        bestBefore: answer.parsedCodeItems.find((x) => x.ai == '15')?.data as Date | undefined,
        expiry: answer.parsedCodeItems.find((x) => x.ai == '17')?.data as Date | undefined,
        quantity: answer.parsedCodeItems.find((x) => x.ai == '37')?.data as number | undefined,
      } as IGS1
    }
  } catch (error) {
    // ignore
    console.debug(error);
  }

  if (props.gs1FieldSelector) {
    changeInputValue(props.gs1FieldSelector(bc, gs1));
  }
  else {
    changeInputValue('');
  }
  
  isLoading.value = false;
  emit('scanned', bc, gs1, undefined);
};

</script>
